// ...existing code...
import React, { useState, useEffect, Suspense, useRef, memo, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { IconType, IconBaseProps } from 'react-icons';
import { FaGithub, FaLinkedin, FaTwitter, FaEnvelope, FaDiscord, FaPaperPlane, FaCheck, FaSpinner, FaCertificate, FaGraduationCap, FaRocket, FaBrain, FaCreditCard, FaChartLine, FaPlane, FaFlask, FaBitcoin, FaMicrochip, FaCode, FaRobot, FaBalanceScale, FaChartBar, FaAtom, FaUniversity, FaPython, FaCloud, FaFileAlt, FaCogs, FaSatellite, FaGlobe } from 'react-icons/fa';
import MobileNav from './components/MobileNav';
import SpaceCursor from './components/CustomCursor';
import Certifications from './components/Certifications';
import { ThemeProvider } from './context/ThemeContext';
import { ErrorProvider } from './context/ErrorContext';
import { GlobalStyle } from './styles/App.styles';
import {
  AppContainer,
  CanvasBackground,
  MainContent,
  Section,
  SectionContent,
  SectionTitle,
  Grid,
  Card,
  StoryText,
  TimelineContainer,
  TimelineItem,
  TimelineContent,
  ContactGrid,
  ContactCard,
  SocialLinks,
  SocialButton,
  ContactPreview,
  StatusBadge,
  QuickLinks,
  CourseCarousel,
  CourseList,
  MilestoneGrid,
  MilestoneCard,
  AdditionalProjects
} from './styles/App.styles';
import ContactForm from './components/ContactForm';
import { sendEmail } from './services/emailService';
import CertificationImage from './components/CertificationImage';

import AmazonArchitect from './assets/certs/Amazon_SolutionsArchitectProgram.png';
import CognizantAI from './assets/certs/Cognizant_AIVirtualExperience.png';
import CS50AI from './assets/certs/CS50AI_Certificate.png';
import FreeCodeCampML from './assets/certs/FreeCodeCamp_MLWithPython.png';
import GoldmanSachs from './assets/certs/GoldmanSachsFinTech.png';
import H2VentureCapital from './assets/certs/H2VentureCapital.png';
import HelsinkiAI from './assets/certs/Helsinki_ElementsOfAI.png';
import HPIQuantum from './assets/certs/HPI_RoadToQuantum.png';
import KagglePython from './assets/certs/Kaggle_Python.png';
import KaggleIntroML from './assets/certs/Kaggle_IntroToMachineLearning.png';
import KaggleIntermediateML from './assets/certs/Kaggle_IntermediateMachineLearning.png';
import KaggleEthics from './assets/certs/Kaggle_IntoToAIEthics.png';
import MLDimensionality from './assets/certs/ML_DimensionalityReduction.png';
import NvidiaJetson from './assets/certs/Nvidia_JetsonNano.png';
import RemoteDronePilot from './assets/certs/RemoteDronePilot107Certification.jpg';
import SaylorBitcoin from './assets/certs/SaylorAcademy_BitcoinForDevs.png';
import ThermoFisher from './assets/certs/ThermoFisher_GeneticScience.png';
import VisaToken from './assets/certs/VISA_TokenServiceTech.png';
import WhiteCaseAntitrust from './assets/certs/WhiteNCase_AntiTrust.png';

// Lazy load components
const MissionLogs = React.lazy(() => import('./components/MissionLogs'));
const Hero = React.lazy(() => import('./components/Hero'));
const CelestialBackground = React.lazy(() => import('./components/CelestialBackground'));
const WireframeSatellite = React.lazy(() => import('./components/WireframeSatellite'));

// Icon wrapper component
interface IconWrapperProps {
  icon: IconType;
  size?: number;
}

// Memoize the IconWrapper component
const IconWrapper = memo<IconWrapperProps>(({ icon: Icon, size = 20 }) => {
  const IconComponent = Icon as React.ComponentType<IconBaseProps>;
  return <IconComponent size={size} />;
});

// Memoize the MilestoneCard component
const MemoizedMilestoneCard = memo(({ 
  title, 
  status, 
  items, 
  delay,
  onCertClick 
}: {
  title: string;
  status: 'completed' | 'in-progress';
  items: Array<{
    icon: IconType;
    name: string;
    date?: string;
    color: string
  }>;
  delay: number;
  onCertClick: (certName: string) => void;
}) => (
  <MilestoneCard
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    status={status}
  >
    <h3>
      <span className={`status-dot ${status}`} />
      {title}
      <span className="status-text">{status === 'completed' ? 'Completed' : ''}</span>
    </h3>
    <div className="cert-container">
      {items.map((item, index) => (
        <div key={index} className="cert-item" onClick={() => onCertClick(item.name)}>
          <div className="icon">
            <IconWrapper icon={item.icon} size={18} />
          </div>
          <div className="cert-details">
            <div className="cert-name">{item.name}</div>
            {item.date && <div className="cert-date">{item.date}</div>}
          </div>
        </div>
      ))}
    </div>
  </MilestoneCard>
));

// Memoize the ContactCard component
const MemoizedContactCard = memo(({ 
  title, 
  children, 
  delay 
}: { 
  title: string; 
  children: React.ReactNode;
  delay: number;
}) => (
  <ContactCard
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
  >
    <h3>{title}</h3>
    {children}
  </ContactCard>
));

// Define AppContent as a function component
const AppContent: React.FC = () => {
  const [launchComplete] = useState(true);
  const [currentSection, setCurrentSection] = useState(0);
  const [showLogs, setShowLogs] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Site is permanently dark, theme context is fixed
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [selectedCertImage, setSelectedCertImage] = useState<string | null>(null);

  const handleCardHover = useCallback((cardIndex: number | null) => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    if (cardIndex === null) {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredCard(null);
      }, 150);
    } else {
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredCard(cardIndex);
      }, 100);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!launchComplete) return;
      
      const sections = document.querySelectorAll('section');
      const totalSections = sections.length;

      switch (e.key) {
        case 'ArrowUp':
          if (currentSection > 0) {
            setCurrentSection(prev => {
              const newSection = prev - 1;
              sections[newSection]?.scrollIntoView({ behavior: 'smooth' });
              return newSection;
            });
          }
          break;
        case 'ArrowDown':
          if (currentSection < totalSections - 1) {
            setCurrentSection(prev => {
              const newSection = prev + 1;
              sections[newSection]?.scrollIntoView({ behavior: 'smooth' });
              return newSection;
            });
          }
          break;
      }
  }, [launchComplete, currentSection]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    mainContentRef.current?.focus();
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleSectionChange = useCallback((section: number) => {
    setCurrentSection(section);
    const sections = document.querySelectorAll('section');
    sections[section]?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleCertClick = (certName: string) => {
    console.log('Certificate clicked:', certName); // Debug log

    const certImageMap: { [key: string]: string } = {
      'White & Case Antitrust': WhiteCaseAntitrust,
      'Visa Token Service Technology': VisaToken,
      'H2 Venture Capitalism Experience': H2VentureCapital,
      'Goldman Sachs FinTech': GoldmanSachs,
      'Udemy Remote Pilot UAS/UAV Certification Part 107': RemoteDronePilot,
      'ThermoFisher Scientific Genetic Sciences': ThermoFisher,
      'CS120 Bitcoin for Developers 1': SaylorBitcoin,
      'NVIDIA AI on Jetson Nano': NvidiaJetson,
      'IBM Machine Learning Dimensionality Reduction': MLDimensionality,
      'Kaggle Python': KagglePython,
      'Kaggle Machine Learning Introduction': KaggleIntroML,
      'Kaggle Introduction to AI Ethics': KaggleEthics,
      'Kaggle Intermediate Machine Learning': KaggleIntermediateML,
      'FreeCodeCamp Machine Learning with Python': FreeCodeCampML,
      'Harvard CS50 with AI': CS50AI,
      'Cognizant AI': CognizantAI,
      'Amazon Solutions Architect': AmazonArchitect
    };

    const imagePath = certImageMap[certName];
    console.log('Mapped image path:', imagePath); // Debug log

    if (imagePath) {
      setSelectedCertImage(imagePath);
    } else {
      console.warn(`No image found for certification: ${certName}`);
    }
  };

  return (
    <AppContainer>
      <SpaceCursor isDark={true} />
      <CanvasBackground />
      <MainContent ref={mainContentRef} tabIndex={-1}>
        <Canvas style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none' }}>
          <CelestialBackground />
          <WireframeSatellite />
        </Canvas>
        <MobileNav 
          onSectionChange={handleSectionChange} 
        />

        {/* Hero Section */}
        <Section>
          <Suspense fallback={null}>
            {typeof window !== 'undefined' && window.innerWidth > 768 ? <Hero /> : null}
          </Suspense>
        </Section>

        {/* Journey Section */}
        <Section>
          <SectionContent>
            <SectionTitle>My Journey</SectionTitle>
            <TimelineContainer>

                  <TimelineItem
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                  >
                    <div className="timeline-dot" />
                    <TimelineContent 
                      isMinimized={hoveredCard !== null && hoveredCard !== 0}
                      onMouseEnter={() => handleCardHover(0)}
                      onMouseLeave={() => handleCardHover(null)}
                    >
                      <div className="journey-header">
                        <h3>Researcher @ XDLabs, Embry-Riddle University</h3>
                        <span className="journey-date">Jan 2026 - June 2026</span>
                      </div>
                      <p className="journey-tagline">Researched feasibility of Monte Carlo based policy-to-risk calculation of orbital stability</p>
                      <div className="journey-details">
                        <ul>
                          <li>
                            <span className="bullet">•</span>
                            Developed a Monte Carlo simulation framework to assess orbital stability and collision risk for satellites, incorporating real-world space debris data and space governance policies.
                          </li>
                          <li>
                            <span className="bullet">•</span>
                            Presented findings at the Penn State Research Exhibition, highlighting the implications of active orbital debris mitigation.
                          </li>
                        </ul>
                      </div>
                    </TimelineContent>
                  </TimelineItem>

                  <TimelineItem
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                  >
                    <div className="timeline-dot" />
                    <TimelineContent 
                      isMinimized={hoveredCard !== null && hoveredCard !== 1}
                      onMouseEnter={() => handleCardHover(1)}
                      onMouseLeave={() => handleCardHover(null)}
                    >
                      <div className="journey-header">
                        <h3>Systems Engineer @ NASA NPWEE</h3>
                        <span className="journey-date">Aug 2025 - Dec 2025</span>
                      </div>
                      <p className="journey-tagline">Led systems integration for a NASA-backed thermal protection system prototype</p>
                      <div className="journey-details">
                        <ul>
                          <li>
                            <span className="bullet">•</span>
                            Optimized hypersonic eddy control to achieve 20% reduction in local heat flux through real-time embedded sensors and active flow response.
                          </li>
                          <li>
                            <span className="bullet">•</span>
                            Performed model analysis in ANSYS Fluent, COMSOL, and MATLAB for firmware development, sensor-actuator calibration, and thermal-structural coupling.
                          </li>
                        </ul>
                      </div>
                    </TimelineContent>
                  </TimelineItem>

                  <TimelineItem
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                  >
                    <div className="timeline-dot" />
                    <TimelineContent 
                      isMinimized={hoveredCard !== null && hoveredCard !== 2}
                      onMouseEnter={() => handleCardHover(2)}
                      onMouseLeave={() => handleCardHover(null)}
                    >
                      <div className="journey-header">
                        <h3>Research Intern @ ISRO Indian Space Academy for Astrophysics</h3>
                        <span className="journey-date">June 2025 - Aug 2025</span>
                      </div>
                      <p className="journey-tagline">Astrophysics research on cosmic expansion and galaxy cluster dynamics</p>
                      <div className="journey-details">
                        <ul>
                          <li>
                            <span className="bullet">•</span>
                            Quantified the cosmic expansion rate by analyzing Type Ia supernova redshift data, applying linear regression to determine the Hubble constant within ±0.2 km/s/Mpc of published values.
                          </li>
                          <li>
                            <span className="bullet">•</span>
                            Estimated dynamical masses of galaxy clusters via velocity-dispersion analysis using Python and statistical modeling.
                          </li>
                        </ul>
                      </div>
                    </TimelineContent>
                  </TimelineItem>

                  <TimelineItem
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
                  >
                    <div className="timeline-dot" />
                    <TimelineContent 
                      isMinimized={hoveredCard !== null && hoveredCard !== 3}
                      onMouseEnter={() => handleCardHover(3)}
                      onMouseLeave={() => handleCardHover(null)}
                    >
                      <div className="journey-header">
                        <h3>Systems Engineer @ NASA Mission Concept Academy</h3>
                        <span className="journey-date">Jan 2025 - May 2025</span>
                      </div>
                      <p className="journey-tagline">Directed systems architecture for a NASA-backed Venus Aerobot mission concept</p>
                      <div className="journey-details">
                        <ul>
                          <li>
                            <span className="bullet">•</span>
                            Led an interdisciplinary team of undergraduate to PhD-level engineers through NASA's formal mission lifecycle, producing flight-ready system architectures for Venus atmospheric exploration.
                          </li>
                          <li>
                            <span className="bullet">•</span>
                            Delivered technical assets including heatflow simulations, mission risk analyses, and Siemens NX CAD designs for a Venus Aerobot, to a panel of NASA scientists.
                          </li>
                        </ul>
                      </div>
                    </TimelineContent>
                  </TimelineItem>

                  <TimelineItem
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 }}
                  >
                    <div className="timeline-dot" />
                    <TimelineContent 
                      isMinimized={hoveredCard !== null && hoveredCard !== 4}
                      onMouseEnter={() => handleCardHover(4)}
                      onMouseLeave={() => handleCardHover(null)}
                    >
                      <div className="journey-header">
                        <h3>Avionics Engineer @ Penn State Rocket Labs</h3>
                        <span className="journey-date">Oct 2024 - Present</span>
                      </div>
                      <p className="journey-tagline">Designing and integrating flight avionics, telemetry, and propulsion systems for the IREC Spaceport America Cup 2025</p>
                      <div className="journey-details">
                        <ul>
                          <li>
                            <span className="bullet">•</span>
                            Integrated onboard power distribution, sensor integration, and real-time data downlink systems for PSU's competition rocket “Judith V2,” enabling precise flight data acquisition and control during suborbital launch.
                          </li>
                        </ul>
                      </div>
                    </TimelineContent>
                  </TimelineItem>

                </TimelineContainer>
              </SectionContent>
            </Section>

            {/* Project Universe Section */}
            <Section>
              <SectionContent>
                <SectionTitle>Project Hub</SectionTitle>
                <Grid className="projects-grid">
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="featured-project"
                  >
                    <div className="project-header">
                      <h3>CubeSat Prototype Launch</h3>
                      <span className="project-tag">Aerospace Systems</span>
                    </div>
                    <ul>
                      <li>
                        Designed and documented a modular CubeSat architecture in a 300+ page Preliminary Design Report, detailing subsystem integration, mission constraints, and risk mitigation strategies.
                      </li>
                      <li>
                        Built, tested, and launched a DIY CubeSat prototype with functioning payload, via high-altitude balloons to 35 km.
                      </li>
                    </ul>
                  </Card>

                  {/* Satellite Ground Station */}
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    className="featured-project"
                  >
                    <div className="project-header">
                      <h3>Satellite Ground Station</h3>
                      <span className="project-tag">Satellite Communications</span>
                    </div>
                    <ul>
                      <li>
                        Designed and deployed Penn State's first student-accessible satellite tracking station, enabling real-time reception of satellite telemetry.
                      </li>
                      <li>
                        Developed an open-access interface allowing students to schedule passes, track satellites, and decode signals through the global SatNOGS network.
                      </li>
                    </ul>
                  </Card>

                  {/* Mars Satellite to Surface Mapping */}
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    className="featured-project"
                  >
                    <div className="project-header">
                      <h3>Mars Satellite-to-Surface 3D Terrain Mapping</h3>
                      <span className="project-tag">Computer Vision</span>
                    </div>
                    <ul>
                      <li>
                        Developed and open-sourced a monocular depth estimation model to convert single satellite images into 3D terrain maps for planetary navigation and hazard detection.
                      </li>
                      <li>
                        Applied model to NASA MRO datasets to identify elevation gradients, landing site obstacles, and potential hazard zones.
                      </li>
                    </ul>
                  </Card>
                </Grid>

                <AdditionalProjects>
                  <div className="header">
                    <h3>Additional Projects</h3>
                    <div className="line" />
                  </div>
                  <div className="projects-grid">
                    {[
                      "Neuroevolutionary Rover Navigation",
                      "Rocket Nozzle CFD Optimization",
                      "SpaceX Virtual Autodock Simulator",
                      "Computer Vision Object Detection",
                      "Elliptic Curve Digital Signature Algorithm Node",
                    ].map((project, index) => (
                      <div key={index} className="project-item">
                        <span className="project-name">{project}</span>
                      </div>
                    ))}
                  </div>
                </AdditionalProjects>
              </SectionContent>
            </Section>

            {/* Leadership, Volunteering & Awards Section */}
            <Section>
              <SectionContent>
                <SectionTitle>Leadership & Volunteering</SectionTitle>
                <Grid
                  className="leadership-awards-grid"
                  style={{
                    maxHeight: '450px',
                    overflowY: 'auto',
                    display: 'grid',
                    gap: '1rem',
                    gridTemplateColumns: typeof window !== 'undefined' && window.innerWidth <= 768 ? '1fr' : 'repeat(2, 1fr)',
                  }}
                >
                  {/* Leadership and Volunteering cards stacked vertically on mobile */}
                  {typeof window !== 'undefined' && window.innerWidth <= 768 ? (
                    <>
                      <Card className="leadership-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <div style={{ fontWeight: 700, color: '#64ffda', marginBottom: '0.5rem', fontSize: '1.1em', letterSpacing: '0.5px' }}>Leadership</div>
                        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Founder, Kepler’s Path</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Created a STEM education nonprofit focused on astronomy and aerospace outreach. Developed visual and interactive curriculum content, reaching over 100 K-12 students in local schools and 2,000+ digital learners worldwide.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Avionics Engineer, Penn State Rocket Labs</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Designed and integrated avionics and telemetry systems for the award-winning Spaceport America Cup rocket submission, contributing to the team’s engineering success and launch readiness.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Aerodynamics Team Member, Nittany Motorsports (Formula SAE)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Collaborated on computational fluid dynamics and aerodynamic optimization for a collegiate race car, improving performance and handling.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Policy Contributor, Space Generation Advisory Council (SGAC)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Contributed to drafting policy recommendations aligned with United Nations space sustainability goals, advocating for responsible space use among the global space community.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, American Institute of Aeronautics and Astronautics (AIAA)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active participant in AIAA, engaging in aerospace leadership initiatives and technical conferences to advance industry knowledge and professional development.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, American Society of Mechanical Engineers (ASME)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active member of ASME, participating in engineering society events and recognized for contributions to mechanical engineering excellence.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, Royal Aeronautical Society (RAeS)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Engaged with RAeS to stay at the forefront of aeronautical engineering advancements and participate in professional development programs.</span>
                          </li>
                        </ul>
                      </Card>
                      <Card className="volunteering-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                        <div style={{ fontWeight: 700, color: '#64ffda', marginBottom: '0.5rem', fontSize: '1.1em', letterSpacing: '0.5px' }}>Volunteering</div>
                        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>NASA Citizen Scientist (LIGO Gravitational Waveform Classifier)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Contributed over 1,500 hours classifying gravitational wave signals to support NASA-LIGO research initiatives, aiding the global scientific community in gravitational wave astronomy.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>STEM Outreach Volunteer</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Participated in workshops and educational programs to inspire and mentor high school and middle school students in science, technology, engineering, and math (STEM) fields.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Pulsar Search Collaboratory</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Assisted in radio astronomy data analysis to identify new pulsar candidates, contributing to ongoing astrophysics research.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Community Organizer, Campus Tech Talks</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Helped coordinate technical seminars and speaker sessions at university, facilitating knowledge exchange among students and faculty.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, Institute of Electrical and Electronics Engineers (IEEE)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active IEEE member, supporting STEM outreach and participating in technical communities focused on electrical engineering and innovation.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Congressional Gold Award (In Progress)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Pursuing one of the highest civilian awards for young Americans, completing 800+ hours of leadership, community service, personal development, and exploration activities.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>On track for Dean’s List at Pennsylvania State University</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Consistently recognized for academic excellence in aerospace engineering coursework.</span>
                          </li>
                        </ul>
                      </Card>
                    </>
                  ) : (
                    <>
                      <Card className="leadership-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <div style={{ fontWeight: 700, color: '#64ffda', marginBottom: '0.5rem', fontSize: '1.1em', letterSpacing: '0.5px' }}>Leadership</div>
                        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Founder & Director, Kepler’s Path</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Created a STEM education nonprofit focused on astronomy and aerospace outreach. Developed visual and interactive curriculum content, reaching over 100 K-12 students in local schools and 2,000+ digital learners worldwide.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Avionics Engineer, Penn State Rocket Labs</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Designed and integrated avionics and telemetry systems for the award-winning Spaceport America Cup rocket submission, contributing to the team’s engineering success and launch readiness.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Aerodynamics Team Member, Nittany Motorsports (Formula SAE)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Collaborated on computational fluid dynamics and aerodynamic optimization for a collegiate race car, improving performance and handling.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Policy Contributor, Space Generation Advisory Council (SGAC)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Contributed to drafting policy recommendations aligned with United Nations space sustainability goals, advocating for responsible space use among the global space community.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, American Institute of Aeronautics and Astronautics (AIAA)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active participant in AIAA, engaging in aerospace leadership initiatives and technical conferences to advance industry knowledge and professional development.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, American Society of Mechanical Engineers (ASME)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active member of ASME, participating in engineering society events and recognized for contributions to mechanical engineering excellence.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, Royal Aeronautical Society (RAeS)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Engaged with RAeS to stay at the forefront of aeronautical engineering advancements and participate in professional development programs.</span>
                          </li>
                        </ul>
                      </Card>
                      <Card className="volunteering-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}>
                        <div style={{ fontWeight: 700, color: '#64ffda', marginBottom: '0.5rem', fontSize: '1.1em', letterSpacing: '0.5px' }}>Volunteering</div>
                        <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>NASA Citizen Scientist (LIGO Gravitational Waveform Classifier)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Contributed over 1,500 hours classifying gravitational wave signals to support NASA-LIGO research initiatives, aiding the global scientific community in gravitational wave astronomy.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>STEM Outreach Volunteer</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Participated in workshops and educational programs to inspire and mentor high school and middle school students in science, technology, engineering, and math (STEM) fields.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Pulsar Search Collaboratory</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Assisted in radio astronomy data analysis to identify new pulsar candidates, contributing to ongoing astrophysics research.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Community Organizer, Campus Tech Talks</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Helped coordinate technical seminars and speaker sessions at university, facilitating knowledge exchange among students and faculty.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Member, Institute of Electrical and Electronics Engineers (IEEE)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Active IEEE member, supporting STEM outreach and participating in technical communities focused on electrical engineering and innovation.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>Congressional Gold Award (In Progress)</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Pursuing one of the highest civilian awards for young Americans, completing 800+ hours of leadership, community service, personal development, and exploration activities.</span>
                          </li>
                          <li style={{ marginBottom: '0.75rem' }}>
                            <strong style={{ fontWeight: 600 }}>On track for Dean’s List at Pennsylvania State University</strong><br />
                            <span style={{ color: '#b0b0b0', fontSize: '0.95em' }}>Consistently recognized for academic excellence in aerospace engineering coursework.</span>
                          </li>
                        </ul>
                      </Card>
                    </>
                  )}
                </Grid>
              </SectionContent>
        </Section>
        {/* Skills Section */}
        <Section>
          <SectionContent>
            <SectionTitle>Technical Arsenal</SectionTitle>
            <Grid className="skills-grid" style={{ gap: '0.75rem', marginBottom: '1.25rem' }}>
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    style={{ padding: '1rem 0.75rem' }}
                  >
                    <h3 style={{ fontSize: '1.08em', marginBottom: '0.5rem' }}>Aerospace & Systems Engineering</h3>
                    <ul style={{ marginBottom: 0, paddingLeft: '1.1em', fontSize: '0.97em', lineHeight: '1.45' }}>
                      <li style={{ marginBottom: '0.35rem' }}>Systems Engineering & Mission Design (NASA MCE, MBSE, SysML)</li>
                      <li style={{ marginBottom: '0.35rem' }}>Orbital Mechanics & Mission Planning</li>
                      <li style={{ marginBottom: '0.35rem' }}>Satellite Tracking & Ground Station Operations (SatNOGS, SDR)</li>
                      <li style={{ marginBottom: '0.35rem' }}>CAD/FEA: Siemens NX, Fusion 360, ANSYS Fluent</li>
                      <li style={{ marginBottom: 0 }}>Embedded Systems: Arduino, Raspberry Pi, CAN Bus</li>
                    </ul>
                  </Card>
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ padding: '1rem 0.75rem' }}
                  >
                    <h3 style={{ fontSize: '1.08em', marginBottom: '0.5rem' }}>Computational Tools & Programming</h3>
                    <ul style={{ marginBottom: 0, paddingLeft: '1.1em', fontSize: '0.97em', lineHeight: '1.45' }}>
                      <li style={{ marginBottom: '0.35rem' }}>Python: mission automation, data analysis, flight software</li>
                      <li style={{ marginBottom: '0.35rem' }}>Additional Programming: MATLAB, Simulink, C++, SQL, Git</li>
                      <li style={{ marginBottom: '0.35rem' }}>Simulation & Modeling: OpenRocket, Orbital Simulators, MATLAB Simulink</li>
                      <li style={{ marginBottom: '0.35rem' }}>AI/ML: TensorFlow, PyTorch, OpenCV, Qiskit</li>
                      <li style={{ marginBottom: 0 }}>Data Analysis & Visualization: Pandas, Matplotlib, Plotly</li>
                    </ul>
                  </Card>
                  <Card
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ padding: '1rem 0.75rem' }}
                  >
                    <h3 style={{ fontSize: '1.08em', marginBottom: '0.5rem' }}>Communication & Leadership</h3>
                    <ul style={{ marginBottom: 0, paddingLeft: '1.1em', fontSize: '0.97em', lineHeight: '1.45' }}>
                      <li style={{ marginBottom: '0.35rem' }}>STEM Education & Outreach (developed space & AI curriculums)</li>
                      <li style={{ marginBottom: '0.35rem' }}>Technical Writing for Engineering Proposals (PDR/CDR)</li>
                      <li style={{ marginBottom: '0.35rem' }}>Public Speaking (STEM conferences & outreach events)</li>
                      <li style={{ marginBottom: 0 }}>Curriculum Design for Aerospace and AI Workshops</li>
                    </ul>
                  </Card>
              </Grid>

              <CourseCarousel>
                  <div className="header" style={{ padding: '0.35rem 0.5rem 0.15rem 0.5rem' }}>
                    <h3 style={{ marginBottom: '0.15rem', fontSize: '1.08em' }}>Penn State Coursework</h3>
                    <div className="line" style={{ marginBottom: '0.15rem' }} />
                  </div>
                  <div className="scroll-container" style={{ padding: '0.35rem 0.5rem 0.25rem 0.5rem' }}>
                    <CourseList
                      animate={{ x: ["0%", "-50%"] }}
                      transition={{
                        duration: 30,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                      style={{ gap: '0.45rem' }}
                    >
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MATH 230: Calculus III</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 301: Aero Structures</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 309: Astronautics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 311: Aerodynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>PHYS 214: Quantum Mechanics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>ME 201: Thermodynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>EMCH 212: Dynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>ASTRO 296: Pulsar Research</span>
                      {/* Duplicate for seamless loop */}
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MATH 230: Calculus III</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 301: Aero Structures</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 309: Astronautics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>AERSP 311: Aerodynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>PHYS 214: Quantum Mechanics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>ME 201: Thermodynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>EMCH 212: Dynamics</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>ASTRO 296: Pulsar Research</span>
                    </CourseList>
                  </div>
              </CourseCarousel>

              <CourseCarousel>
                  <div className="header" style={{ padding: '0.35rem 0.5rem 0.15rem 0.5rem' }}>
                    <h3 style={{ marginBottom: '0.15rem', fontSize: '1.08em' }}>Audited Coursework</h3>
                    <div className="line" style={{ marginBottom: '0.15rem' }} />
                  </div>
                  <div className="scroll-container" style={{ padding: '0.35rem 0.5rem 0.25rem 0.5rem' }}>
                    <CourseList
                      animate={{ x: ["0%", "-50%"] }}
                      transition={{
                        duration: 35,
                        ease: "linear",
                        repeat: Infinity,
                      }}
                      style={{ gap: '0.45rem' }}
                    >
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 6.006: Introduction to Algorithms</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Stanford CS224N: Natural Language Processing</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 16.687: Private Pilot Group School</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Stanford CS229: Machine Learning</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 6.S191: Deep Learning</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Harvard CS50x: Computer Science</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 16.00: Introduction to Aerospace Engineering</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>PSU AERSP 309: Astronautics</span>
                      {/* Duplicate for seamless loop */}
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 6.006: Introduction to Algorithms</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Stanford CS224N: Natural Language Processing</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 16.687: Private Pilot Group School</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Stanford CS229: Machine Learning</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 6.S191: Deep Learning</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>Harvard CS50x: Computer Science</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>MIT 16.00: Introduction to Aerospace Engineering</span>
                      <span className="course-item" style={{ padding: '0.3rem 0.7rem' }}>PSU AERSP 309: Astronautics</span>
                    </CourseList>
                  </div>
            </CourseCarousel>
          </SectionContent>
        </Section>

        {/* Certificates Section */}
        <Section>
          <SectionContent>
            <SectionTitle>Milestones</SectionTitle>
            <MilestoneGrid>
                  {/* Stack certifications vertically on mobile */}
                  {typeof window !== 'undefined' && window.innerWidth <= 768 ? (
                    <>
                      <MemoizedMilestoneCard
                        title="In Progress"
                        status="in-progress"
                        items={[
                          {
                            icon: FaRocket,
                            name: "Tripoli L1 High Power Rocketry Certification",
                            date: "Expected: December 2026",
                            color: "#ffd700"
                          },
                          {
                            icon: FaSpinner,
                            name: "Siemens NX Design Associate",
                            date: "Expected: September 2026",
                            color: "#ffd700"
                          },
                          {
                            icon: FaCogs,
                            name: "FreeFlyer Mission Design & Analysis Certification",
                            date: "Expected: August 2026",
                            color: "#ffd700"
                          },
                          {
                            icon: FaSatellite,
                            name: "AGI Systems Tool Kit (STK) Fundamentals",
                            date: "Expected: August 2026",
                            color: "#ffd700"
                          }
                        ]}
                        delay={0}
                        onCertClick={handleCertClick}
                      />
                      <MilestoneCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        status="completed"
                        style={{ maxHeight: '450px', overflowY: 'auto', padding: '1.5rem 1rem' }}
                      >
                        <h3>
                          <span className="status-dot completed" />
                          Completed Certifications
                        </h3>
                        <div style={{ height: '2px', backgroundColor: '#64ffda', margin: '0.5rem 0' }} />
                        <div className="cert-section" style={{ marginBottom: '1.25rem' }}>
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>Aerospace & Space Systems</h4>
                          <div className="cert-container">
                            {[{ icon: FaRocket, name: "Robotics in Space (MIT)", color: "#64ffda" },
                              { icon: FaGlobe, name: "NASA L'Space Project Management & Systems Engineering", color: "#64ffda" },
                              { icon: FaPlane, name: "Udemy Remote Pilot UAS/UAV Certification Part 107", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div className="cert-section" style={{ marginBottom: '1.25rem' }}>
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>AI / Data Science</h4>
                          <div className="cert-container">
                            {[{ icon: FaGraduationCap, name: "Harvard CS50 with AI", color: "#64ffda" },
                              { icon: FaBrain, name: "IBM Machine Learning Dimensionality Reduction", color: "#64ffda" },
                              { icon: FaMicrochip, name: "NVIDIA AI on Jetson Nano", color: "#64ffda" },
                              { icon: FaPython, name: "FreeCodeCamp Machine Learning with Python", color: "#64ffda" },
                              { icon: FaRobot, name: "Cognizant AI", color: "#64ffda" },
                              { icon: FaChartBar, name: "Kaggle Intermediate Machine Learning", color: "#64ffda" },
                              { icon: FaChartLine, name: "Kaggle Machine Learning Introduction", color: "#64ffda" },
                              { icon: FaBalanceScale, name: "Kaggle Introduction to AI Ethics", color: "#64ffda" },
                              { icon: FaCode, name: "Kaggle Python", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div className="cert-section">
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>Technology / Other Domains</h4>
                          <div className="cert-container">
                            {[{ icon: FaCloud, name: "Amazon Solutions Architect", color: "#64ffda" },
                              { icon: FaFlask, name: "ThermoFisher Scientific Genetic Sciences", color: "#64ffda" },
                              { icon: FaCreditCard, name: "Visa Token Service Technology", color: "#64ffda" },
                              { icon: FaAtom, name: "OpenHPI Future of Computing: Road to Quantum", color: "#64ffda" },
                              { icon: FaUniversity, name: "Helsinki University Elements of AI", color: "#64ffda" },
                              { icon: FaBitcoin, name: "CS120 Bitcoin for Developers 1", color: "#64ffda" },
                              { icon: FaChartLine, name: "Goldman Sachs FinTech", color: "#64ffda" },
                              { icon: FaChartLine, name: "H2 Venture Capitalism Experience", color: "#64ffda" },
                              { icon: FaCertificate, name: "White & Case Antitrust", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </MilestoneCard>
                    </>
                  ) : (
                    <>
                      <MemoizedMilestoneCard
                        title="In Progress"
                        status="in-progress"
                        items={[
                          {
                            icon: FaRocket,
                            name: "Tripoli L1 High Power Rocketry Certification",
                            date: "Expected: December 2025",
                            color: "#ffd700"
                          },
                          {
                            icon: FaSpinner,
                            name: "Siemens NX Design Associate",
                            date: "Expected: September 2025",
                            color: "#ffd700"
                          },
                          {
                            icon: FaCogs,
                            name: "FreeFlyer Mission Design & Analysis Certification",
                            date: "Expected: August 2025",
                            color: "#ffd700"
                          },
                          {
                            icon: FaSatellite,
                            name: "AGI Systems Tool Kit (STK) Fundamentals",
                            date: "Expected: August 2025",
                            color: "#ffd700"
                          }
                        ]}
                        delay={0}
                        onCertClick={handleCertClick}
                      />
                      <MilestoneCard
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        status="completed"
                        style={{ maxHeight: '450px', overflowY: 'auto', padding: '1.5rem 1rem' }}
                      >
                        <h3>
                          <span className="status-dot completed" />
                          Completed Certifications
                        </h3>
                        <div style={{ height: '2px', backgroundColor: '#64ffda', margin: '0.5rem 0' }} />
                        <div className="cert-section" style={{ marginBottom: '1.25rem' }}>
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>Aerospace & Space Systems</h4>
                          <div className="cert-container">
                            {[{ icon: FaRocket, name: "Robotics in Space (MIT)", color: "#64ffda" },
                              { icon: FaGlobe, name: "NASA L'Space Project Management & Systems Engineering", color: "#64ffda" },
                              { icon: FaPlane, name: "Udemy Remote Pilot UAS/UAV Certification Part 107", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div className="cert-section" style={{ marginBottom: '1.25rem' }}>
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>AI / Data Science</h4>
                          <div className="cert-container">
                            {[{ icon: FaGraduationCap, name: "Harvard CS50 with AI", color: "#64ffda" },
                              { icon: FaBrain, name: "IBM Machine Learning Dimensionality Reduction", color: "#64ffda" },
                              { icon: FaMicrochip, name: "NVIDIA AI on Jetson Nano", color: "#64ffda" },
                              { icon: FaPython, name: "FreeCodeCamp Machine Learning with Python", color: "#64ffda" },
                              { icon: FaRobot, name: "Cognizant AI", color: "#64ffda" },
                              { icon: FaChartBar, name: "Kaggle Intermediate Machine Learning", color: "#64ffda" },
                              { icon: FaChartLine, name: "Kaggle Machine Learning Introduction", color: "#64ffda" },
                              { icon: FaBalanceScale, name: "Kaggle Introduction to AI Ethics", color: "#64ffda" },
                              { icon: FaCode, name: "Kaggle Python", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                        <div className="cert-section">
                          <h4 style={{ color: '#64ffda', marginBottom: '0.5rem' }}>Technology / Other Domains</h4>
                          <div className="cert-container">
                            {[{ icon: FaCloud, name: "Amazon Solutions Architect", color: "#64ffda" },
                              { icon: FaFlask, name: "ThermoFisher Scientific Genetic Sciences", color: "#64ffda" },
                              { icon: FaCreditCard, name: "Visa Token Service Technology", color: "#64ffda" },
                              { icon: FaAtom, name: "OpenHPI Future of Computing: Road to Quantum", color: "#64ffda" },
                              { icon: FaUniversity, name: "Helsinki University Elements of AI", color: "#64ffda" },
                              { icon: FaBitcoin, name: "CS120 Bitcoin for Developers 1", color: "#64ffda" },
                              { icon: FaChartLine, name: "Goldman Sachs FinTech", color: "#64ffda" },
                              { icon: FaChartLine, name: "H2 Venture Capitalism Experience", color: "#64ffda" },
                              { icon: FaCertificate, name: "White & Case Antitrust", color: "#64ffda" }].map((item, index) => (
                                <div key={index} className="cert-item" onClick={() => handleCertClick(item.name)}>
                                  <div className="icon">
                                    <IconWrapper icon={item.icon} size={18} />
                                  </div>
                                  <div className="cert-details">
                                    <div className="cert-name">{item.name}</div>
                                  </div>
                                </div>
                              ))}
                          </div>
                        </div>
                      </MilestoneCard>
                    </>
                  )}
            </MilestoneGrid>
          </SectionContent>
        </Section>

        {/* Contact Section */}
        <Section>
          <SectionContent>
            <SectionTitle>Let's Connect</SectionTitle>
            <ContactGrid>
                  <MemoizedContactCard title="Send a Message" delay={0}>
                    <p>Want to discuss a project or just say hello? Drop me a message!</p>
                    <ContactForm onSubmit={sendEmail} />
                  </MemoizedContactCard>

                  <MemoizedContactCard title="Connect With Me" delay={0.2}>
                    <p>I'm always excited to discuss new opportunities and collaborations in aerospace, robotics, and software development.</p>
                    
                    <SocialLinks>
                      <SocialButton href="https://github.com/buzzpranav" target="_blank">
                        <IconWrapper icon={FaGithub} size={20} /> GitHub
                      </SocialButton>
                      <SocialButton href="https://www.linkedin.com/in/pranav-s-narayanan/" target="_blank">
                        <IconWrapper icon={FaLinkedin} size={20} /> LinkedIn
                      </SocialButton>
                      <SocialButton href="https://www.linkedin.com/in/pranav-s-narayanan/overlay/1754854945726/single-media-viewer/" target="_blank">
                        <IconWrapper icon={FaFileAlt} size={20} /> Resume
                      </SocialButton>
                      <SocialButton href="mailto:buzzpranav06@gmail.com">
                        <IconWrapper icon={FaEnvelope} size={20} /> Email
                      </SocialButton>
                    </SocialLinks>

                    <StatusBadge>
                      Available for Collaboration
                    </StatusBadge>
                  </MemoizedContactCard>
            </ContactGrid>
          </SectionContent>
        </Section>

        <AnimatePresence>
          {showLogs && (
            <Suspense fallback={null}>
              <MissionLogs
                currentSection={currentSection}
                onClose={() => setShowLogs(false)}
              />
            </Suspense>
          )}
        </AnimatePresence>

        {/* ThemeToggle removed: site is permanently dark */}
      </MainContent>

      <CertificationImage
        imagePath={selectedCertImage || ''}
        isOpen={!!selectedCertImage}
        onClose={() => setSelectedCertImage(null)}
      />
    </AppContainer>
  );
};

// Define App as a function component
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ErrorProvider>
      <GlobalStyle />
      <AppContent />
      </ErrorProvider>
    </ThemeProvider>
  );
};

// Export the App component
export default App;